import clsx from 'clsx';
import {
  Briefcase,
  CreditCard,
  Database,
  DollarSign,
  Globe,
  Home,
  Info,
  Key,
  Layers,
  Languages,
  LayoutGrid,
  Megaphone,
  Activity,
  TrendingUp,
  Settings2,
  Sparkles,
  Tag,
  UserCircle,
  Users,
} from 'lucide-react';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useApplication } from '../context/ApplicationContext';
import { useIsPro } from '../hooks/useIsPro';
import { useDebugMode } from '../hooks/useDebugMode';
import ProTag from './ProTag';
import { SkeletonBar } from './Skeleton';
import { PANEL_BASE, panelPath } from '../config/paths';

interface SidebarProps {
  isOpen: boolean;
}

type NavItem = {
  nameKey: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  debugOnly?: boolean;
  section: 'workspace' | 'tools' | 'system';
};

const navKeys: NavItem[] = [
  { nameKey: 'nav.dashboard', href: PANEL_BASE, icon: Home, section: 'workspace' },
  { nameKey: 'nav.applications', href: panelPath('/applications'), icon: Layers, section: 'workspace' },
  { nameKey: 'nav.jobs', href: panelPath('/jobs'), icon: Briefcase, section: 'workspace' },
  { nameKey: 'nav.secrets', href: panelPath('/secrets'), icon: Key, section: 'tools' },
  { nameKey: 'nav.teamUsers', href: panelPath('/team-users'), icon: UserCircle, section: 'tools' },
  { nameKey: 'nav.storage', href: panelPath('/storage'), icon: Database, section: 'tools' },
  { nameKey: 'nav.publishers', href: panelPath('/publishers'), icon: Activity, section: 'tools' },
  { nameKey: 'nav.appleSearchAds', href: panelPath('/apple-search-ads'), icon: Megaphone, section: 'tools' },
  { nameKey: 'nav.aso', href: panelPath('/aso'), icon: TrendingUp, section: 'tools' },
  { nameKey: 'nav.aiLocalization', href: panelPath('/localization'), icon: Languages, section: 'tools', debugOnly: true },
  { nameKey: 'nav.systemDetails', href: panelPath('/system-details'), icon: Settings2, debugOnly: true, section: 'system' },
];

const SECTION_ORDER = ['workspace', 'tools', 'system'] as const;
const SECTION_LABEL: Record<(typeof SECTION_ORDER)[number], string> = {
  workspace: 'nav.sectionWorkspace',
  tools: 'nav.sectionTools',
  system: 'nav.sectionSystem',
};

const SYSTEM_MEMBERSHIPS_HREF = panelPath('/system-details/memberships');
const SYSTEM_OVERVIEW_HREF = panelPath('/system-details/overview');
const SYSTEM_DETAILS_BASE = panelPath('/system-details');

const appDetailPathRegex = /^\/panel\/applications\/([^/]+)$/;
const appScreensPathRegex = /^\/panel\/applications\/([^/]+)\/app-screens/;
const appPricingPathRegex = /^\/panel\/applications\/([^/]+)\/pricing$/;
const appAvailabilityPathRegex = /^\/panel\/applications\/([^/]+)\/availability$/;
const appRevenueCatPathRegex = /^\/panel\/applications\/([^/]+)\/revenuecat$/;
const appAppleSearchAdsPathRegex = /^\/panel\/applications\/([^/]+)\/apple-search-ads$/;
const appASOPathRegex = /^\/panel\/applications\/([^/]+)\/aso$/;
const versionPathRegex = /^\/panel\/applications\/([^/]+)\/versions\/([^/]+)(?:\/([^/]+))?/;

function getAppIdFromPath(pathname: string): string | null {
  const m = pathname.match(/^\/panel\/applications\/([^/]+)/);
  return m ? m[1] : null;
}

/** /panel/applications/:id/versions/:versionId or .../versions/:versionId/:tab → { versionId, tab }. */
function getVersionFromPath(pathname: string): { versionId: string; tab: string } | null {
  const m = pathname.match(versionPathRegex);
  if (!m) return null;
  return { versionId: m[2], tab: 'information' };
}

const APPLICATIONS_HREF = panelPath('/applications');

const Sidebar: React.FC<SidebarProps> = ({ isOpen }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const { isPro, goToUpgrade } = useIsPro();
  const hasDebugMode = useDebugMode();
  const showReactivate = user?.canReactivateSubscription === true;
  const showUpgrade = !isPro;
  const isAppDetailPage = appDetailPathRegex.test(location.pathname);
  const isAppScreensPage = appScreensPathRegex.test(location.pathname);
  const isAppPricingPage = appPricingPathRegex.test(location.pathname);
  const isAppAvailabilityPage = appAvailabilityPathRegex.test(location.pathname);
  const isAppRevenueCatPage = appRevenueCatPathRegex.test(location.pathname);
  const isAppAppleSearchAdsPage = appAppleSearchAdsPathRegex.test(location.pathname);
  const isAppASOPage = appASOPathRegex.test(location.pathname);
  const appId = getAppIdFromPath(location.pathname);
  const versionRoute = getVersionFromPath(location.pathname);
  const versionId = versionRoute?.versionId ?? null;
  const showAppSubMenu = Boolean(appId);
  const showSystemSubMenu =
    hasDebugMode &&
    (location.pathname === SYSTEM_DETAILS_BASE ||
      location.pathname.startsWith(`${SYSTEM_DETAILS_BASE}/`));
  const isSystemMembershipsPage = location.pathname === SYSTEM_MEMBERSHIPS_HREF;
  const isSystemOverviewPage = location.pathname === SYSTEM_OVERVIEW_HREF;

  const {
    app: contextApp,
    versions,
    versionsLoading,
  } = useApplication();
  const appName = appId && contextApp?.id === appId ? contextApp.name : null;
  const appType = appId && contextApp?.id === appId ? (contextApp.type ?? null) : null;

  const isMobilApp = appType === 'mobil_app';

  const linkCls = (active: boolean, nested = false) =>
    clsx(
      'relative flex items-center gap-2.5 rounded-lg text-sm font-medium transition-colors group text-left w-full',
      nested ? 'px-2.5 py-1.5' : 'px-2.5 py-2',
      active
        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-gray-100'
    );

  const iconCls = (active: boolean, nested = false) =>
    clsx(
      nested ? 'h-4 w-4 shrink-0' : 'h-[18px] w-[18px] shrink-0',
      active
        ? 'text-primary-600 dark:text-primary-400'
        : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'
    );

  const sectionLabelCls =
    'px-2.5 pb-1.5 pt-4 first:pt-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500';

  const nestedSectionLabelCls =
    'px-2.5 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400/90 dark:text-gray-500';

  const renderAppSubMenu = () => {
    if (!showAppSubMenu || !appId) return null;

    return (
      <div className="mt-1 mb-1 ml-3 pl-3 border-l border-gray-200 dark:border-gray-700 space-y-0.5">
        <div
          className="px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 truncate"
          title={appName ?? appId}
        >
          {appName ?? (appId.length > 16 ? `${appId.slice(0, 16)}…` : appId)}
        </div>

        <div className={nestedSectionLabelCls}>{t('nav.general')}</div>
        <Link to={panelPath(`/applications/${appId}`)} className={linkCls(isAppDetailPage, true)}>
          <Info className={iconCls(isAppDetailPage, true)} />
          <span>{t('common.detail')}</span>
        </Link>

        {isMobilApp && (
          <>
            <div className={nestedSectionLabelCls}>{t('nav.marketing')}</div>
            <Link
              to={panelPath(`/applications/${appId}/app-screens`)}
              className={linkCls(isAppScreensPage, true)}
            >
              <LayoutGrid className={iconCls(isAppScreensPage, true)} />
              <span>{t('nav.appScreen')}</span>
            </Link>

            <div className={nestedSectionLabelCls}>{t('nav.store')}</div>
            {isPro ? (
              <Link
                to={panelPath(`/applications/${appId}/pricing`)}
                className={linkCls(isAppPricingPage, true)}
              >
                <DollarSign className={iconCls(isAppPricingPage, true)} />
                <span>{t('nav.pricing')}</span>
              </Link>
            ) : (
              <button
                type="button"
                onClick={goToUpgrade}
                className={clsx(linkCls(false, true), 'opacity-70')}
              >
                <DollarSign className={iconCls(false, true)} />
                <span className="flex-1 text-left">{t('nav.pricing')}</span>
                <ProTag />
              </button>
            )}
            <Link
              to={panelPath(`/applications/${appId}/availability`)}
              className={linkCls(isAppAvailabilityPage, true)}
            >
              <Globe className={iconCls(isAppAvailabilityPage, true)} />
              <span>{t('nav.availability')}</span>
            </Link>
            <Link
              to={panelPath(`/applications/${appId}/revenuecat`)}
              className={linkCls(isAppRevenueCatPage, true)}
            >
              <CreditCard className={iconCls(isAppRevenueCatPage, true)} />
              <span>{t('nav.revenueCat')}</span>
            </Link>
            <Link
              to={panelPath(`/applications/${appId}/apple-search-ads`)}
              className={linkCls(isAppAppleSearchAdsPage, true)}
            >
              <Megaphone className={iconCls(isAppAppleSearchAdsPage, true)} />
              <span>{t('nav.appleSearchAds')}</span>
            </Link>
            <Link to={panelPath(`/applications/${appId}/aso`)} className={linkCls(isAppASOPage, true)}>
              <TrendingUp className={iconCls(isAppASOPage, true)} />
              <span>{t('nav.aso')}</span>
            </Link>

            <div className={nestedSectionLabelCls}>{t('nav.version')}</div>
            {versionsLoading ? (
              <>
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-2 px-2.5 py-1.5">
                    <SkeletonBar className="h-4 w-4 rounded flex-shrink-0" />
                    <SkeletonBar width="40%" className="h-3.5" />
                  </div>
                ))}
              </>
            ) : versions.length === 0 ? (
              <div className="px-2.5 py-1.5 text-xs text-gray-500 dark:text-gray-400">
                {t('nav.noList')}
              </div>
            ) : (
              versions.map(v => {
                const isVersionActive = versionId === v.id;
                return (
                  <Link
                    key={v.id}
                    to={panelPath(`/applications/${appId}/versions/${v.id}/information`)}
                    className={linkCls(isVersionActive, true)}
                  >
                    <Tag className={iconCls(isVersionActive, true)} />
                    <span>v{v.version}</span>
                  </Link>
                );
              })
            )}
          </>
        )}
      </div>
    );
  };

  const renderSystemSubMenu = () => {
    if (!showSystemSubMenu) return null;
    return (
      <div className="mt-1 mb-1 ml-3 pl-3 border-l border-gray-200 dark:border-gray-700 space-y-0.5">
        <Link to={SYSTEM_OVERVIEW_HREF} className={linkCls(isSystemOverviewPage, true)}>
          <Activity className={iconCls(isSystemOverviewPage, true)} />
          <span>{t('nav.systemOverview')}</span>
        </Link>
        <Link to={SYSTEM_MEMBERSHIPS_HREF} className={linkCls(isSystemMembershipsPage, true)}>
          <Users className={iconCls(isSystemMembershipsPage, true)} />
          <span>{t('nav.systemMemberships')}</span>
        </Link>
      </div>
    );
  };

  const renderNavItem = (item: NavItem) => {
    if (item.debugOnly && !hasDebugMode) return null;

    const isApplications = item.href === APPLICATIONS_HREF;
    const isActive = !isApplications && location.pathname === item.href;
    const isApplicationsActive =
      isApplications && (location.pathname === APPLICATIONS_HREF || showAppSubMenu);

    if (isApplications) {
      return (
        <div key={item.nameKey}>
          <Link to={item.href} className={linkCls(isApplicationsActive)}>
            <item.icon className={iconCls(isApplicationsActive)} />
            <span className="truncate">{t(item.nameKey)}</span>
          </Link>
          {renderAppSubMenu()}
        </div>
      );
    }

    if (item.nameKey === 'nav.systemDetails') {
      return (
        <div key={item.nameKey}>
          <Link to={SYSTEM_OVERVIEW_HREF} className={linkCls(showSystemSubMenu)}>
            <item.icon className={iconCls(showSystemSubMenu)} />
            <span className="truncate">{t(item.nameKey)}</span>
          </Link>
          {renderSystemSubMenu()}
        </div>
      );
    }

    return (
      <Link key={item.nameKey} to={item.href} className={linkCls(isActive)}>
        <item.icon className={iconCls(isActive)} />
        <span className="truncate">{t(item.nameKey)}</span>
      </Link>
    );
  };

  const visibleBySection = SECTION_ORDER.map(section => ({
    section,
    items: navKeys.filter(item => {
      if (item.section !== section) return false;
      if (item.debugOnly && !hasDebugMode) return false;
      return true;
    }),
  })).filter(group => group.items.length > 0);

  return (
    <aside
      className={clsx(
        'fixed left-0 top-14 h-[calc(100vh-3.5rem)] w-64 z-40 flex flex-col',
        'bg-white/95 dark:bg-gray-800/95 backdrop-blur-md supports-[backdrop-filter]:bg-white/80 dark:supports-[backdrop-filter]:bg-gray-800/80',
        'border-r border-gray-200/80 dark:border-gray-700/80',
        'transition-transform duration-300 ease-out text-left',
        isOpen ? 'translate-x-0' : '-translate-x-full'
      )}
    >
      <nav className="flex-1 overflow-y-auto overscroll-contain px-2.5 py-3 space-y-0.5">
        {visibleBySection.map(({ section, items }) => (
          <div key={section}>
            <div className={sectionLabelCls}>{t(SECTION_LABEL[section])}</div>
            <div className="space-y-0.5">{items.map(renderNavItem)}</div>
          </div>
        ))}
      </nav>

      {(showReactivate || showUpgrade) && (
        <div className="shrink-0 border-t border-gray-100 dark:border-gray-700/80 p-3">
          {showReactivate ? (
            <div className="rounded-xl bg-amber-50/90 dark:bg-amber-900/20 border border-amber-200/80 dark:border-amber-800/60 p-3">
              <Link
                to={panelPath('profile')}
                className="flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 transition-colors"
              >
                <Sparkles className="h-4 w-4 shrink-0" />
                {t('nav.reactivateSubscription')}
              </Link>
              <p className="mt-2 text-[11px] leading-relaxed text-amber-700/90 dark:text-amber-300/90">
                {t('nav.reactivateSubscriptionHint')}
              </p>
            </div>
          ) : (
            <div className="rounded-xl bg-primary-50/90 dark:bg-primary-900/20 border border-primary-200/80 dark:border-primary-800/60 p-3">
              <Link
                to={panelPath('upgrade')}
                className="upgrade-btn-glow flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg text-white text-sm font-medium transition-colors"
              >
                <Sparkles className="h-4 w-4 shrink-0" />
                {t('nav.upgradeToPro')}
              </Link>
              <p className="mt-2 text-[11px] leading-relaxed text-primary-700/90 dark:text-primary-300/90">
                {t('nav.upgradeToProHint')}
              </p>
            </div>
          )}
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
